import { useEffect, useState } from "react";
import { DownloadSimple, X, Share, Plus } from "@phosphor-icons/react";

/**
 * Native "Add to Home Screen" prompt for supporting browsers (Chrome/Edge/Android),
 * plus an iOS Safari fallback panel with manual instructions.
 *
 * Dismissal is remembered in localStorage so we don't nag users on every load.
 */
const DISMISS_KEY = "fairway:install-dismissed-at";
const DISMISS_DAYS = 14;

function isIos() {
  if (typeof window === "undefined") return false;
  return /iPhone|iPad|iPod/i.test(window.navigator.userAgent) && !window.MSStream;
}
function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}
function wasRecentlyDismissed() {
  try {
    const ts = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (!ts) return false;
    return Date.now() - ts < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIosSheet, setShowIosSheet] = useState(false);

  useEffect(() => {
    if (isStandalone() || wasRecentlyDismissed()) return;

    const onBefore = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBefore);

    // iOS doesn't fire beforeinstallprompt — show our own helper banner after a short delay.
    let iosTimer;
    if (isIos()) {
      iosTimer = setTimeout(() => setVisible(true), 2500);
    }

    const onInstalled = () => {
      setVisible(false);
      setDeferred(null);
    };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBefore);
      window.removeEventListener("appinstalled", onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => {
    setVisible(false);
    setShowIosSheet(false);
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {}
  };

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      setDeferred(null);
      setVisible(false);
      if (outcome === "dismissed") dismiss();
    } else if (isIos()) {
      setShowIosSheet(true);
    }
  };

  if (!visible) return null;

  return (
    <>
      <div
        data-testid="install-prompt"
        className="fixed left-0 right-0 z-50 px-3 pointer-events-none"
        style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 96px)" }}
      >
        <div className="max-w-md mx-auto pointer-events-auto">
          <div
            className="flex items-center gap-3 rounded-2xl p-3 shadow-[0_18px_50px_-12px_rgba(20,61,47,0.4)] border"
            style={{ background: "var(--primary)", color: "#F2EEE7", borderColor: "rgba(255,255,255,0.08)" }}
          >
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
              style={{ background: "rgba(242,238,231,0.12)" }}
            >
              <DownloadSimple size={22} weight="bold" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-heading text-sm font-semibold leading-tight">
                Install Fairway
              </div>
              <div className="text-[11px] opacity-80 leading-snug">
                Add to home screen for offline rounds & faster launch.
              </div>
            </div>
            <button
              data-testid="install-prompt-install"
              onClick={install}
              className="px-3 py-2 rounded-xl text-xs font-bold font-heading uppercase tracking-wide"
              style={{ background: "#C4543C", color: "#F2EEE7" }}
            >
              Install
            </button>
            <button
              data-testid="install-prompt-dismiss"
              onClick={dismiss}
              aria-label="Dismiss install prompt"
              className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
              style={{ background: "rgba(242,238,231,0.08)" }}
            >
              <X size={16} weight="bold" />
            </button>
          </div>
        </div>
      </div>

      {showIosSheet && (
        <div
          data-testid="install-ios-sheet"
          className="fixed inset-0 z-[60] flex items-end justify-center"
          style={{ background: "rgba(20,61,47,0.55)" }}
          onClick={dismiss}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-t-3xl p-5 pb-8"
            style={{ background: "var(--surface, #F2EEE7)", color: "var(--primary)" }}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="font-heading text-lg font-bold">Install on iPhone</div>
                <div className="text-xs opacity-70">Two taps and Fairway lives on your home screen.</div>
              </div>
              <button
                onClick={dismiss}
                aria-label="Close"
                className="w-8 h-8 rounded-full flex items-center justify-center"
                style={{ background: "rgba(20,61,47,0.08)" }}
              >
                <X size={16} weight="bold" />
              </button>
            </div>
            <ol className="space-y-3 mt-2">
              <li className="flex items-center gap-3 text-sm">
                <span
                  className="w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs"
                  style={{ background: "var(--primary)", color: "#F2EEE7" }}
                >
                  1
                </span>
                <span className="flex-1">
                  Tap the <Share size={16} weight="bold" className="inline mx-1" /> Share button in Safari.
                </span>
              </li>
              <li className="flex items-center gap-3 text-sm">
                <span
                  className="w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs"
                  style={{ background: "var(--primary)", color: "#F2EEE7" }}
                >
                  2
                </span>
                <span className="flex-1">
                  Choose <strong>Add to Home Screen</strong>{" "}
                  <Plus size={16} weight="bold" className="inline mx-1" />.
                </span>
              </li>
              <li className="flex items-center gap-3 text-sm">
                <span
                  className="w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs"
                  style={{ background: "var(--primary)", color: "#F2EEE7" }}
                >
                  3
                </span>
                <span className="flex-1">Hit <strong>Add</strong> — done.</span>
              </li>
            </ol>
          </div>
        </div>
      )}
    </>
  );
}
